import { useState, useEffect } from "react";
import { base44 } from "../../api/base44Client";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Collapse,
  Grid,
  Alert,
} from "@mui/material";
import {
  Link2,
  Mail,
  MessageSquare,
  Copy,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Send,
} from "lucide-react";

export default function WizardSharePanel({
  recordId,
  praticaId,
  customerEmail,
  customerName,
}) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState(customerEmail || "");
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    if (open && recordId) loadLink();
  }, [open, recordId]);

  useEffect(() => {
    if (customerEmail) setEmail(customerEmail);
  }, [customerEmail]);

  async function loadLink() {
    setLoading(true);
    const links = await base44.entities.CustomerLink.filter({
      financing_request_id: recordId,
    });
    const active = links.find(
      (l) => !["revoked", "expired"].includes(l.status),
    );
    setLink(active || null);
    setLoading(false);
  }

  async function handleGenerate() {
    if (!recordId) return;
    setGenerating(true);
    await base44.functions.invoke("generateCustomerLink", {
      financing_request_id: recordId,
      expiration_days: 7,
      customer_email: email,
      customer_name: customerName,
    });
    await loadLink();
    setGenerating(false);
  }

  async function handleSendEmail() {
    if (!link) return;
    setSending(true);
    setSendResult(null);
    const res = await base44.functions.invoke("sendCustomerLink", {
      link_id: link.id,
      method: "email",
    });
    setSendResult(res.data);
    setSending(false);
  }

  function intakeUrl() {
    if (!link) return "";
    return `${window.location.origin}/intake/${link.token}`;
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(intakeUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function whatsappText() {
    const name = (customerName || "Cliente").split(" ")[0];
    return `Ciao ${name}, ti inviamo il link per completare la tua richiesta di finanziamento. Abbiamo già inserito parte dei dati. Ti basterà verificare e completare quelli mancanti: ${intakeUrl()}`;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 2, overflow: "hidden", borderRadius: 3 }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          bgcolor: "action.hover",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Send size={18} style={{ color: "#2196f3" }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Condividi questionario con il cliente
          </Typography>
        </Box>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </Box>

      <Collapse in={open}>
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          {!recordId ? (
            <Typography variant="caption" color="text.secondary">
              Salva prima la pratica per poter generare il link.
            </Typography>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} color="inherit" />
            </Box>
          ) : !link ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Genera un link sicuro da inviare al cliente per completare il
                questionario.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Email cliente"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerate}
                disabled={generating}
                startIcon={
                  generating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Link2 size={16} />
                  )
                }
                fullWidth
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Genera link
              </Button>
            </>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  wordBreak: "break-all",
                  userSelect: "all",
                  fontFamily: "monospace",
                  fontSize: "0.625rem",
                  color: "text.secondary",
                }}
              >
                {intakeUrl()}
              </Paper>

              <TextField
                fullWidth
                size="small"
                placeholder="Email cliente"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
              />

              {sendResult && !sendResult.email_sent && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ borderRadius: 2, py: 0 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, display: "block" }}
                  >
                    Email non inviata automaticamente
                  </Typography>
                  <Typography variant="caption">
                    Condividi il link manualmente tramite copia o WhatsApp.
                  </Typography>
                </Alert>
              )}
              {sendResult && sendResult.email_sent && (
                <Alert
                  severity="success"
                  variant="outlined"
                  sx={{ borderRadius: 2, py: 0 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Email inviata con successo!
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={handleCopy}
                    startIcon={
                      copied ? (
                        <CheckCircle2 size={16} color="success" />
                      ) : (
                        <Copy size={16} />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontSize: "0.7rem",
                    }}
                  >
                    {copied ? "Copiato!" : "Copia"}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={handleSendEmail}
                    disabled={sending || !email}
                    startIcon={
                      sending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Mail size={16} />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontSize: "0.7rem",
                    }}
                  >
                    Email
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    component="a"
                    href={`https://wa.me/?text=${encodeURIComponent(
                      whatsappText(),
                    )}`}
                    target="_blank"
                    startIcon={<MessageSquare size={16} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontSize: "0.7rem",
                    }}
                  >
                    WhatsApp
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
